import { UNDO_ACTION, REDO_ACTION, SAVE_CANVAS_STATE, CLEAR_HISTORY, CANVAS_OBJECT_COUNT_CHANGED, CANVAS_FORM_CHANGED, UPDATE_CANVAS_SIZE, CANVAS_HOLES_CHANGED, CANVAS_PRICE_RERENDERED, UPLOADED_IMAGES, } from '../../Rutab/RutabConstants';
import { overrideRotationControl, setVisibilityControlsForObject, } from '../../Editor/CanvasService';
import { availableForms } from '../../Components/Editor/FormMenuContent';
import { handleSizeChange, updateMeasurementPositions, } from '../../Editor/DecalEditorCanvasSizing';
/**
 * Maximum number of history states to keep
 */
const MAX_HISTORY_STATES = 30;
/**
 * Action to save the current canvas state to history
 */
export const saveCanvasState = (canvas) => (dispatch, getState) => {
    const state = getState();
    const historyState = state.decalEditor.historyState;
    const isUndoRedoInProgress = state.decalEditor.isUndoRedoInProgress || false;
    if (!canvas || isUndoRedoInProgress) {
        return;
    }
    try {
        const canvasObject = JSON.stringify(canvas.toDatalessJSON([
            'clipPath',
            'canvasSize',
            'form-id',
            'hole-placement',
            'id',
            'evented',
            'systemId',
            'cart-image',
        ]));
        if (!canvasObject || canvasObject === '{}') {
            return;
        }
        const newHistory = historyState.currentIndex < historyState.history.length - 1
            ? historyState.history.slice(0, historyState.currentIndex + 1)
            : [...historyState.history];
        const updatedHistory = [...newHistory, canvasObject];
        let newIndex = historyState.currentIndex + 1;
        if (updatedHistory.length > MAX_HISTORY_STATES) {
            const excessStates = updatedHistory.length - MAX_HISTORY_STATES;
            updatedHistory.splice(0, excessStates);
            newIndex = Math.max(0, newIndex - excessStates);
        }
        dispatch({
            type: SAVE_CANVAS_STATE,
            payload: {
                historyState: {
                    history: updatedHistory,
                    currentIndex: newIndex,
                },
            },
        });
    }
    catch (error) {
        console.error('Error saving canvas state:', error);
    }
};
/**
 * Action to perform undo operation
 */
export const undoAction = (canvas, canvasRef) => (dispatch, getState) => {
    const state = getState();
    const historyState = state.decalEditor.historyState;
    if (historyState.currentIndex <= 0 || !canvas) {
        console.warn('Cannot undo: at start of history or canvas not ready');
        return;
    }
    dispatch({
        type: UNDO_ACTION,
        payload: {
            isUndoRedoInProgress: true,
        },
    });
    const newIndex = historyState.currentIndex - 1;
    const canvasStateStr = historyState.history[newIndex];
    if (!canvasStateStr) {
        console.warn('No valid canvas state found for undo at index:', newIndex);
        dispatch({
            type: UNDO_ACTION,
            payload: {
                isUndoRedoInProgress: false,
            },
        });
        return;
    }
    try {
        canvas
            .loadFromJSON(JSON.parse(canvasStateStr), () => { })
            .then((loadedCanvas) => {
            restoreHistoryState(loadedCanvas, canvasRef, state)(dispatch);
            dispatch({
                type: UNDO_ACTION,
                payload: {
                    historyState: Object.assign(Object.assign({}, historyState), { currentIndex: newIndex }),
                    isUndoRedoInProgress: false,
                },
            });
            loadedCanvas.renderAll();
        });
    }
    catch (error) {
        console.error('Error during undo:', error);
        dispatch({
            type: UNDO_ACTION,
            payload: {
                isUndoRedoInProgress: false,
            },
        });
    }
};
/**
 * Action to perform redo operation
 */
export const redoAction = (canvas, canvasRef) => (dispatch, getState) => {
    const state = getState();
    const historyState = state.decalEditor.historyState;
    if (historyState.currentIndex >= historyState.history.length - 1 || !canvas) {
        console.warn('Cannot redo: at end of history or canvas not ready');
        return;
    }
    dispatch({
        type: REDO_ACTION,
        payload: {
            isUndoRedoInProgress: true,
        },
    });
    const newIndex = historyState.currentIndex + 1;
    const canvasStateStr = historyState.history[newIndex];
    if (!canvasStateStr) {
        console.warn('No valid canvas state found for redo at index:', newIndex);
        dispatch({
            type: REDO_ACTION,
            payload: {
                isUndoRedoInProgress: false,
            },
        });
        return;
    }
    try {
        canvas
            .loadFromJSON(JSON.parse(canvasStateStr), () => { })
            .then((loadedCanvas) => {
            restoreHistoryState(loadedCanvas, canvasRef, state)(dispatch);
            dispatch({
                type: REDO_ACTION,
                payload: {
                    historyState: Object.assign(Object.assign({}, historyState), { currentIndex: newIndex }),
                    isUndoRedoInProgress: false,
                },
            });
            loadedCanvas.renderAll();
        });
    }
    catch (error) {
        console.error('Error during redo:', error);
        dispatch({
            type: REDO_ACTION,
            payload: {
                isUndoRedoInProgress: false,
            },
        });
    }
};
/**
 * Action to clear history
 */
export const clearHistory = () => ({
    type: CLEAR_HISTORY,
    payload: {
        historyState: {
            history: [],
            currentIndex: -1,
        },
    },
});
const restoreHistoryState = (loadedCanvas, canvasRef, state) => (dispatch) => {
    loadedCanvas.getObjects().forEach((x) => {
        (x.borderColor = 'none'),
            (x.cornerColor = '#112d61'),
            (x.cornerSize = 12),
            (x.cornerStyle = 'circle'),
            (x.transparentCorners = false),
            (x.centeredScaling = true),
            (x.padding = 0),
            setVisibilityControlsForObject(x);
        overrideRotationControl(x);
        if (x.type === 'textbox') {
            x.set({
                breakWords: true,
                wordWrap: true,
            });
        }
    });
    let needToRerenderPrice = true;
    const objectCount = loadedCanvas
        .getObjects()
        .filter((o) => o.id !== 'hole-group').length;
    if (state.decalEditor.objectCount !== objectCount) {
        needToRerenderPrice = false;
        dispatch({
            type: CANVAS_OBJECT_COUNT_CHANGED,
            payload: {
                objectCount: objectCount,
            },
        });
    }
    const canvasFormId = loadedCanvas.get('form-id');
    const canvasForm = availableForms.find((x) => x.formKey == canvasFormId);
    const holePlacement = loadedCanvas.get('hole-placement');
    dispatch({
        type: CANVAS_FORM_CHANGED,
        payload: { canvasForm },
    });
    dispatch({
        type: CANVAS_HOLES_CHANGED,
        payload: { holePlacement },
    });
    const canvasSizeState = state.decalEditor.canvasSize;
    const canvasCanvasSize = loadedCanvas.get('canvasSize');
    if (canvasSizeState.width !== canvasCanvasSize.width ||
        canvasSizeState.height !== canvasCanvasSize.height) {
        needToRerenderPrice = false;
        handleSizeChange(canvasCanvasSize, loadedCanvas);
        updateMeasurementPositions(canvasCanvasSize.height, canvasCanvasSize.width, canvasRef);
        dispatch({
            type: UPDATE_CANVAS_SIZE,
            payload: { canvasSize: canvasCanvasSize },
        });
    }
    if (needToRerenderPrice) {
        dispatch({
            type: CANVAS_PRICE_RERENDERED,
        });
    }
    if (loadedCanvas.getObjects().filter((o) => o.type === 'image').length > 0) {
        const uniqueImages = Array.from(loadedCanvas
            .getObjects()
            .filter((obj) => obj.type === 'image' && (obj === null || obj === void 0 ? void 0 : obj.get('systemId')))
            .reduce((map, obj) => {
            map.set(obj === null || obj === void 0 ? void 0 : obj.get('systemId'), obj === null || obj === void 0 ? void 0 : obj.get('cart-image'));
            return map;
        }, new Map())
            .values());
        dispatch({
            type: UPLOADED_IMAGES,
            payload: { uploadedImages: uniqueImages },
        });
    }
    else {
        dispatch({
            type: UPLOADED_IMAGES,
            payload: { uploadedImages: [] },
        });
    }
};
